import { AuthProvider, Options } from 'react-admin'
import { fetchUtils } from 'ra-core'
import ReactGA from 'react-ga4'
import { hotjar } from 'react-hotjar'
import { CATEGORY_USER, EVENT_LOGGED_IN } from './constants'

const apiUrl = process.env.REACT_APP_ADMIN_API
const tokenTtl = 30 // minutes
const defaultCurrency = 'GBP'
const defaultNoun = 'SPEND'

const getAccessToken = () => {
  const token = localStorage.getItem('access_token')
  return Promise.resolve(token)
}

const httpClient = (url, options: Options = {}) => {
  const noptions = options

  // Ensure cookies (e.g., trusted-device) are sent with requests
  if (!('credentials' in noptions)) {
    // @ts-ignore - Options from ra-core allow credentials passthrough
    noptions.credentials = 'include'
  }

  if (!noptions.headers) {
    noptions.headers = new Headers({ Accept: 'application/json' })
  }
  return getAccessToken().then((token) => {
    if (token) {
      noptions.headers = new Headers(noptions.headers)
      noptions.headers.set('Authorization', `Bearer ${token}`)
    }
    return fetchUtils.fetchJson(url, noptions)
  })
}

const authProvider: AuthProvider = {
  login: ({ username, password }) => {
    const url = `${apiUrl}/auth/jwt/login`

    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)

    return httpClient(url, {
      method: 'POST',
      body: formData,
    })
      .then(({ json }) => {
        localStorage.setItem('access_token', json.access_token)
        const dt = new Date()
        localStorage.setItem('access_token_claimed', dt.toString())
        dt.setMinutes(dt.getMinutes() + tokenTtl)
        localStorage.setItem('access_token_expires', dt.toString())

        // persist filters for a user via LocalStorage
        const savedTxnFilters = localStorage.getItem(
          username.concat('_', 'txnFilters')
        )
        const savedTxnFiltersIdx = localStorage.getItem(
          username.concat('_', 'txnFilters_idx')
        )
        if (savedTxnFilters) {
          localStorage.setItem('txnFilters', savedTxnFilters)
          if (savedTxnFiltersIdx) {
            localStorage.setItem('txnFilters_idx', savedTxnFiltersIdx)
          } else {
            localStorage.setItem('txnFilters_idx', '-1')
          }
        }

        // Check if 2FA verification is required for this device/session
        return httpClient(`${apiUrl}/2fa/status`, {
          method: 'GET',
        })
          .then(({ json: statusJson }) => {
            const requires = !!statusJson?.requires_verification
            if (requires) {
              const redirect = statusJson?.redirect || '/verify-2fa'
              window.location.href = redirect
              return Promise.reject(
                new Error(
                  redirect === '/setup-2fa'
                    ? '2FA setup required'
                    : '2FA verification required'
                )
              )
            }
            // Fetch user info after confirming 2FA is not required, and store it
            return httpClient(`${apiUrl}/users/me`, { method: 'GET' }).then(
              ({ json: userJson }) => {
                const details = { fullName: userJson.name, ...userJson }
                localStorage.setItem('user', JSON.stringify(details))
                localStorage.setItem(
                  'defaultCurrency',
                  details.organisation?.currency?.code || defaultCurrency
                )
                localStorage.setItem(
                  'defaultNoun',
                  details.organisation?.noun || defaultNoun
                )
                return Promise.resolve()
              }
            )
          })
          .catch((error) => {
            console.error('Error checking 2FA status during login:', error)
            return Promise.resolve() // Continue login process even if we can't check 2FA status
          })
      })
      .catch((error) => {
        console.error('Login error:', error)
        return Promise.reject(error || 'Login failed')
      })
  },
  logout: async () => {
    // persist filters for a user via LocalStorage
    const user = localStorage.getItem('user')
    if (user) {
      const userBits = JSON.parse(user)
      const savedTxnFilters = localStorage.getItem('txnFilters')
      const savedTxnFiltersIdx = localStorage.getItem('txnFilters_idx')
      if (savedTxnFilters) {
        localStorage.setItem(
          userBits.email.concat('_', 'txnFilters'),
          savedTxnFilters
        )
        if (savedTxnFiltersIdx) {
          localStorage.setItem(
            userBits.email.concat('_', 'txnFilters_idx'),
            savedTxnFiltersIdx
          )
        } else {
          localStorage.setItem(
            userBits.email.concat('_', 'txnFilters_idx'),
            '-1'
          )
        }
      }
    }

    // Clear 2FA verification status in Redis
    const token = localStorage.getItem('access_token')
    if (token) {
      try {
        // Make an API call to clear 2FA verification and wait for it to complete
        await httpClient(`${apiUrl}/2fa/clear-verification`, {
          method: 'POST',
        }).catch((error) => {
          console.error('Error clearing 2FA verification:', error)
        })
      } catch (error) {
        console.error('Error clearing 2FA verification:', error)
      }
    }

    // JWT auth has no log out
    // Remove known auth/app keys
    localStorage.removeItem('user')
    localStorage.removeItem('access_token')
    localStorage.removeItem('access_token_claimed')
    localStorage.removeItem('access_token_expires')
    localStorage.removeItem('txnFilters')
    localStorage.removeItem('txnFilters_idx')

    try {
      // Remove React-Admin and app UI state keys that remember last location, preferences, etc.
      // This ensures next user starts with a clean state.
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i) as string
        if (!key) continue
        if (
          key.startsWith('RaStore.') || // react-admin store (including organisation.actingAs, UI params)
          key.startsWith('ra-') || // react-admin cache keys
          key === 'i18nextLng' || // language preference
          key.toLowerCase().includes('last') || // any generic last* keys (e.g., last visited)
          key.toLowerCase().includes('previous')
        ) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k))
    } catch (e) {
      console.warn('Failed to purge stored UI state on logout', e)
    }

    try {
      // Clear session-only state
      sessionStorage.clear()
    } catch (e) {
      console.warn('Failed to clear sessionStorage on logout', e)
    }

    try {
      // Best-effort: clear non-HttpOnly cookies accessible to JS
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((c) => c.length > 0)
        .forEach((c) => {
          const eqPos = c.indexOf('=')
          const name = eqPos > -1 ? c.substring(0, eqPos) : c
          // expire the cookie for current path and root path
          document.cookie = `${name}=; Max-Age=0; path=/`
        })
    } catch (e) {
      console.warn('Failed to clear cookies on logout', e)
    }

    return Promise.resolve()
  },
  checkAuth: async () => {
    // Skip the 2FA check if we're already on a 2FA page
    const currentPath = window.location.pathname
    if (currentPath === '/verify-2fa' || currentPath === '/setup-2fa') {
      return Promise.resolve()
    }

    const token = localStorage.getItem('access_token')
    const tokenClaimed = localStorage.getItem('access_token_claimed')
    const tokenExpires = localStorage.getItem('access_token_expires')

    if (token === null || tokenExpires === null || tokenClaimed === null) {
      localStorage.removeItem('user')
      localStorage.removeItem('access_token')
      localStorage.removeItem('access_token_claimed')
      localStorage.removeItem('access_token_expires')
      localStorage.removeItem('txnFilters')
      localStorage.removeItem('txnFiltersIdx')
      // localStorage.clear()
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ message: false }) // Disables 'Please login to continue' error message on first load
    }

    const dt = new Date(tokenExpires)
    const now = new Date()
    const claimed = new Date(tokenClaimed)

    if (dt < now) {
      localStorage.removeItem('user')
      localStorage.removeItem('access_token')
      localStorage.removeItem('access_token_claimed')
      localStorage.removeItem('access_token_expires')
      localStorage.removeItem('txnFilters')
      localStorage.removeItem('txnFiltersIdx')
      // localStorage.clear()
      return Promise.reject()
    }

    const apiTokenExpires = claimed
    apiTokenExpires.setHours(apiTokenExpires.getHours() + 12)

    const nextNow = now
    nextNow.setMinutes(nextNow.getMinutes() + tokenTtl)

    if (apiTokenExpires < nextNow) {
      localStorage.removeItem('user')
      localStorage.removeItem('access_token')
      localStorage.removeItem('access_token_claimed')
      localStorage.removeItem('access_token_expires')
      localStorage.removeItem('txnFilters')
      localStorage.removeItem('txnFiltersIdx')
      // localStorage.clear()
      return Promise.reject()
    }

    localStorage.setItem('access_token_expires', nextNow.toString())

    // Check 2FA status
    try {
      // Import dataProvider dynamically to avoid circular dependency
      const { default: dataProvider } = await import('./dataProvider')
      const { verified, redirect } = await dataProvider.check2FAStatus()

      if (!verified) {
        // Redirect to the appropriate 2FA page
        if (redirect) {
          window.location.href = redirect
        } else {
          // Default to verification page if redirect is undefined
          window.location.href = '/verify-2fa'
        }
        return Promise.reject(new Error('2FA verification required'))
      }

      return Promise.resolve()
    } catch (error) {
      console.error('Error checking 2FA status:', error)
      // If there's an error checking 2FA status, continue with normal authentication
      // This prevents users from being locked out if there's an issue with the 2FA system
      return Promise.resolve()
    }
  },
  checkError: (error) => {
    const { status } = error
    if (status === 401 || status === 403) {
      //    localStorage.removeItem('access_token');
      //    return Promise.reject();
    }
    // other error code (404, 500, etc): no need to log out
    return Promise.resolve()
  },
  getIdentity: () => {
    const url = `${apiUrl}/users/me`

    // Skip the API call if we're in the 2FA verification flow
    const currentPath = window.location.pathname
    if (currentPath === '/verify-2fa' || currentPath === '/setup-2fa') {
      // Return a minimal user object to satisfy React Admin
      return Promise.resolve({ id: 'temp', fullName: 'Verifying 2FA' })
    }

    const user = localStorage.getItem('user')
    if (user !== null) {
      const parsed = JSON.parse(user)
      // If cached user is missing org currency, refetch to avoid stale defaults (2FA flow can cause this)
      if (!parsed?.organisation?.currency?.code) {
        return httpClient(url, { method: 'GET' })
          .then(({ json }) => {
            const details = { fullName: json.name, ...json }
            localStorage.setItem('user', JSON.stringify(details))
            localStorage.setItem(
              'defaultCurrency',
              details.organisation?.currency?.code || defaultCurrency
            )
            localStorage.setItem(
              'defaultNoun',
              details.organisation?.noun || defaultNoun
            )
            ReactGA.set({ userId: details.id })
            hotjar.identify(details.email, {})
            ReactGA.event({
              category: CATEGORY_USER,
              action: EVENT_LOGGED_IN,
            })
            return Promise.resolve(details)
          })
          .catch(() => Promise.resolve(parsed))
      }
      // Ensure defaults are present even when user is loaded from cache
      localStorage.setItem(
        'defaultCurrency',
        parsed.organisation?.currency?.code || defaultCurrency
      )
      localStorage.setItem(
        'defaultNoun',
        parsed.organisation?.noun || defaultNoun
      )
      return Promise.resolve(parsed)
    }

    return httpClient(url, {
      method: 'GET',
    })
      .then(({ json }) => {
        // munge details to fit react admin
        const details = {
          fullName: json.name,
          ...json,
        }
        localStorage.setItem('user', JSON.stringify(details))
        localStorage.setItem(
          'defaultCurrency',
          details.organisation?.currency?.code || defaultCurrency
        )
        localStorage.setItem(
          'defaultNoun',
          details.organisation?.noun || defaultNoun
        )
        ReactGA.set({ userId: details.id })
        hotjar.identify(details.email, {})
        ReactGA.event({
          category: CATEGORY_USER,
          action: EVENT_LOGGED_IN,
        })
        return Promise.resolve(details)
      })
      .catch(() => {
        return Promise.reject()
      })
  },
  getPermissions: () => {
    const url = `${apiUrl}/users/me`

    // Skip the API call if we're in the 2FA verification flow
    const currentPath = window.location.pathname
    if (currentPath === '/verify-2fa' || currentPath === '/setup-2fa') {
      // Return minimal permissions during 2FA verification
      return Promise.resolve(0)
    }

    const user = localStorage.getItem('user')
    if (user !== null) {
      const u = JSON.parse(user)
      return Promise.resolve(u.permission_flags)
    }

    return httpClient(url, {
      method: 'GET',
    })
      .then(({ json }) => {
        return Promise.resolve(json.permission_flags)
      })
      .catch(() => {
        return Promise.resolve('')
      })
  },
  getAccessToken: () => {
    const token = localStorage.getItem('access_token')
    return Promise.resolve(token)
  },
}

export default authProvider
